import { useEffect, useState } from "react";
import { Loader2, RefreshCcw, FileText } from "lucide-react";
import { api, API, inr } from "../../lib/api";
import { toast } from "sonner";
import { openOrDownloadPdf, openPdfUrl, filenameFromContentDisposition } from "../../lib/pdfDownload";
import PdfViewerModal from "../../lib/PdfViewerModal";

export default function MerchantSettlements() {
    const [data, setData] = useState({ settlements: [], pending_amount: 0, paid_amount: 0 });
    const [busy, setBusy] = useState(false);
    const [pdfModal, setPdfModal] = useState(null);

    const load = () => api.get("/merchant/settlements").then(({ data }) => setData(data));
    useEffect(() => { load(); }, []);

    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/merchant/settlements/run?days_back=7");
            toast.success(`Created ${data.created} settlements`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
    };

    // iter101 — PDF invoice download for the merchant's own settlements.
    // iter134 — routed through the shared ``openOrDownloadPdf`` helper
    // so Capacitor Android WebView users get the OS share sheet (Save
    // to Files / Open in PDF viewer) instead of a silent no-op.
    const downloadInvoice = async (sid) => {
        // iter134m — native: render inline via PdfViewerModal.
        const cap = typeof window !== "undefined" ? window.Capacitor : null;
        if (cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform()) {
            setPdfModal({ endpoint: `/merchant/settlements/${sid}/invoice.pdf`, filename: `payout-${sid}.pdf` });
            return;
        }
        try {
            const { data, headers } = await api.get(
                `/merchant/settlements/${sid}/invoice.pdf`,
                { responseType: "blob" },
            );
            const ctype = (headers["content-type"] || "").toLowerCase();
            if (!ctype.includes("application/pdf")) {
                const text = await data.text();
                toast.error(text?.slice(0, 200) || "Server did not return a PDF");
                return;
            }
            const blob = new Blob([data], { type: "application/pdf" });
            const filename = filenameFromContentDisposition(
                headers["content-disposition"], `payout-${sid}.pdf`,
            );
            await openOrDownloadPdf(blob, filename);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to download invoice");
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div><h1 className="text-2xl font-bold">Settlements & Payouts</h1><p className="text-sm text-muted-foreground mt-1">Net payout = gross − commission − delivery fee collected</p></div>
                <button data-testid="run-settlement" onClick={run} disabled={busy} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />} Run settlement</button>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-6">
                <Card label="Pending payout" v={inr(data.pending_amount)} klass="bg-amber-100 text-amber-700" />
                <Card label="Paid out" v={inr(data.paid_amount)} klass="bg-emerald-100 text-emerald-700" />
            </div>

            <div className="mt-6 bg-card border border-border rounded-2xl overflow-x-auto">
                <table className="w-full text-sm">
                    <thead><tr className="bg-muted/60 text-left text-[11px] uppercase tracking-wider">
                        <th className="px-4 py-3">Period</th><th className="px-4 py-3">Orders</th><th className="px-4 py-3">Gross</th><th className="px-4 py-3">Commission</th><th className="px-4 py-3">Net payable</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Invoice</th>
                    </tr></thead>
                    <tbody>
                        {data.settlements.map((s) => (
                            <tr key={s.id} data-testid={`set-row-${s.id}`} className="border-t border-border">
                                <td className="px-4 py-3 font-semibold">{new Date(s.period_start).toLocaleDateString()}</td>
                                <td className="px-4 py-3">{s.orders_count}</td>
                                <td className="px-4 py-3">{inr(s.gross_sales)}</td>
                                <td className="px-4 py-3 text-red-600">−{inr(s.commission_amount)} <span className="text-xs text-muted-foreground">({s.commission_percent}%)</span></td>
                                <td className="px-4 py-3 font-bold">{inr(s.payable_amount)}</td>
                                <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${s.status === "PAID" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{s.status}</span></td>
                                <td className="px-4 py-3">
                                    {/* iter101 — merchant can download their own payout PDF */}
                                    <button
                                        data-testid={`m-invoice-${s.id}`}
                                        onClick={() => downloadInvoice(s.id)}
                                        className="text-xs font-bold text-primary inline-flex items-center gap-1"
                                    >
                                        <FileText size={12} /> PDF
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {data.settlements.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">No settlements yet. Click &quot;Run settlement&quot; to aggregate past 7 days of delivered orders.</td></tr>}
                    </tbody>
                </table>
            </div>
            {pdfModal && (
                <PdfViewerModal
                    endpoint={pdfModal.endpoint}
                    filename={pdfModal.filename}
                    onClose={() => setPdfModal(null)}
                />
            )}
        </div>
    );
}

function Card({ label, v, klass }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">{label}</p>
            <p className={`text-2xl font-bold mt-1 inline-block px-3 py-0.5 rounded-lg ${klass}`}>{v}</p>
        </div>
    );
}
